/**
 * The groove: where a Cue lands beside its Step, applied by the page at cue
 * time and never on the wire's `due`. The server keeps the grid.
 */

/** The sixteenth at `bpm`, in ms. */
export const stepMs = (bpm: number): number => 15_000 / bpm

/**
 * MPC swing: the sixteenths in pairs, the second of each pair (the e and the a,
 * odd Counts) landing at `swing`% of the pair instead of 50%. 50 is straight,
 * 75 halfway to the next sixteenth. The beats and the ands never move.
 */
export const swingMs = (count: number, swing: number, bpm: number): number =>
  count % 2 === 1 ? ((swing - 50) / 50) * stepMs(bpm) : 0

/*
 * Drift: each Event wandering slowly off the grid and in level, after Hennig,
 * "Synchronization in human musical rhythms and mutually interacting complex
 * systems", PNAS 111(36), 2014, eq. 1, the model James Holden's Group
 * Humanizer is built on. Hennig's two players' intervals are an internal 1/f
 * clock, plus the difference of white motor noise, minus a pull `W` against
 * their running deviation from each other. Summed, the intervals are offsets
 * from a grid, so here each hand's offset follows
 *
 *   O[n+1] = O[n] + C[n+1] + ξ[n+1] − ξ[n] − W·(O[n] − mean of the others' O[n]) − W_grid·O[n]
 *
 * for four hands. The grid is a fifth player that never moves: Hennig's pair
 * has no click, and without `W_grid` the kit as a whole would wander off
 * without bound. `C` is Voss–McCartney 1/f noise, octave k redrawn every 2^k
 * Steps; `ξ` is white. Every hand keeps time every Step, sounding or not.
 * Level has its own Voss–McCartney process per hand, uncoupled, in dB.
 *
 * The page computes Drift rather than the server: it needs no wire, and every
 * Visitor derives the same from `gen`, a page joining mid-walk by running the
 * process forward from Step 0.
 */

/** The four hands: a ghost snare is the snare's. */
export type Hand = "kick" | "snare" | "hat" | "tom"

/** The hands in their fixed draw order. */
const HANDS: ReadonlyArray<Hand> = ["kick", "snare", "hat", "tom"]

export interface DriftConfig {
  /** W: each hand's pull toward the other three. Hennig's pair diverges once their two W sum to 2. */
  readonly coupling: number
  /** W_grid: every hand's pull toward the grid. */
  readonly anchor: number
  /** Voss–McCartney octaves. */
  readonly octaves: number
  readonly timingMsPerUnit: number
  /** The level drift's standard deviation, in dB. */
  readonly velocityDb: number
  /** How far below 1 the level drift is centred, in dB, so a kick at 1 is rarely clipped by the core. */
  readonly headroomDb: number
}

/**
 * Tuned by ear by Tero on 2026-09-27, with the Walker playing: three times the
 * starting depth (3.6 ms a unit, 3 dB, 6 dB headroom) was loose but never
 * sloppy, and half of that was kept, about 7 to 8 ms either side of the grid.
 */
export const DRIFT: DriftConfig = {
  // 0.9 against the starting 0.5: the walk is linear, one hit a Step, so the
  // coupling is heard only as the jump when the hand changes, and anywhere from
  // 0.3 to 0.9 measures about 4 to 5 ms a Step. Liked, if possibly placebo.
  coupling: 0.9,
  // Untouched: the kit's wander stays centred on the grid.
  anchor: 0.1,
  octaves: 12,
  // 1.8, from 1.2: loose rather than a machine.
  timingMsPerUnit: 1.8,
  // 1.5 dB of level drift, from 1, centred 3 dB down, from 2, so a kick rarely
  // reaches the core's clamp at 1.
  velocityDb: 1.5,
  headroomDb: 3
}

export interface Drifted {
  /** Added to the Cue's due. */
  readonly ms: number
  /** Times the Cue's velocity. */
  readonly gain: number
}

/** One walk's Drift. Deterministic in (gen, step, hand), whatever order it is asked in. */
export interface Groove {
  readonly gen: number
  readonly at: (step: number, hand: Hand) => Drifted
}

/** How many Steps behind the newest a Groove keeps; older asks run again from Step 0. */
const RING = 4_096

const mulberry32 = (seed: number) => {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296
  }
}

/** Standard Gaussians by Box–Muller, two per two uniforms. */
const gaussian = (uniform: () => number) => {
  let spare: number | undefined
  return (): number => {
    if (spare !== undefined) {
      const value = spare
      spare = undefined
      return value
    }
    const radius = Math.sqrt(-2 * Math.log(1 - uniform()))
    const angle = 2 * Math.PI * uniform()
    spare = radius * Math.sin(angle)
    return radius * Math.cos(angle)
  }
}

export const groove = (gen: number, config: DriftConfig = DRIFT): Groove => {
  const count = HANDS.length
  const norm = Math.sqrt(config.octaves)
  // The ring of results, in noise units: turned into ms and gain only when asked.
  const timings = new Float64Array(RING * count)
  const levelRing = new Float64Array(RING * count)
  // Each hand's running state, flat: its offset and last motor noise, and its
  // clock's and level's octaves, `count` rows of `octaves`.
  const offsets = new Float64Array(count)
  const motors = new Float64Array(count)
  const clocks = new Float64Array(count * config.octaves)
  const levels = new Float64Array(count * config.octaves)
  // Each hand's clock and level sums, kept as octaves are redrawn.
  const clockSums = new Float64Array(count)
  const levelSums = new Float64Array(count)
  const nextOffsets = new Float64Array(count)
  let draw = gaussian(mulberry32(gen))
  let next = 0

  const reset = () => {
    draw = gaussian(mulberry32(gen))
    offsets.fill(0)
    motors.fill(0)
    clocks.fill(0)
    levels.fill(0)
    clockSums.fill(0)
    levelSums.fill(0)
    next = 0
  }

  // Octave k is redrawn when the Step is a multiple of 2^k, so every octave at
  // Step 0, and the draws stop at the first octave not due. Returns the sum's change.
  const redraw = (octaves: Float64Array, from: number, step: number): number => {
    let change = 0
    for (let k = 0; k < config.octaves; k++) {
      if (step !== 0 && step % 2 ** k !== 0) break
      const value = draw()
      change += value - (octaves[from + k] ?? 0)
      octaves[from + k] = value
    }
    return change
  }

  const advance = () => {
    const step = next
    const slot = (step % RING) * count
    let total = 0
    for (let i = 0; i < count; i++) total += offsets[i] ?? 0
    for (let i = 0; i < count; i++) {
      const clockSum = (clockSums[i] ?? 0) + redraw(clocks, i * config.octaves, step)
      clockSums[i] = clockSum
      const motor = draw()
      const levelSum = (levelSums[i] ?? 0) + redraw(levels, i * config.octaves, step)
      levelSums[i] = levelSum
      const clock = clockSum / norm
      const level = levelSum / norm
      const offset = offsets[i] ?? 0
      const others = (total - offset) / (count - 1)
      nextOffsets[i] =
        offset +
        clock +
        motor -
        (motors[i] ?? 0) -
        config.coupling * (offset - others) -
        config.anchor * offset
      motors[i] = motor
      levelRing[slot + i] = level
    }
    for (let i = 0; i < count; i++) {
      const offset = nextOffsets[i] ?? 0
      offsets[i] = offset
      timings[slot + i] = offset
    }
    next = step + 1
  }

  return {
    gen,
    at: (step, hand) => {
      if (step < next - RING) reset()
      while (next <= step) advance()
      const i = (step % RING) * count + HANDS.indexOf(hand)
      return {
        ms: (timings[i] ?? 0) * config.timingMsPerUnit,
        gain: 10 ** (((levelRing[i] ?? 0) * config.velocityDb - config.headroomDb) / 20)
      }
    }
  }
}
